/**
 * usePendingCounts — trabajo pendiente para el badge del sidebar admin.
 *
 * Un solo fetch compartido por TanStack Query (staleTime 60s, refetch al
 * volver a la tab + cada 2 min con la tab visible). Patrón portado del
 * PendingUsersIndicator de clarimoine-fan.
 */

import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";

export interface PendingCounts {
  memberships_awaiting: number;
  free_pending: number;
  mp_pending: number;
  total: number;
}

export const EMPTY_PENDING_COUNTS: PendingCounts = {
  memberships_awaiting: 0,
  free_pending: 0,
  mp_pending: 0,
  total: 0,
};

export function usePendingCountsQuery(): PendingCounts {
  const { getAuthHeaders, isLoaded } = useAdminAuth();

  const { data } = useQuery<PendingCounts>({
    queryKey: ["admin", "pending-counts"],
    enabled: isLoaded,
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/pending-counts", { headers });
      if (!res.ok) throw new Error(`pending-counts ${res.status}`);
      return (await res.json()) as PendingCounts;
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  return data ?? EMPTY_PENDING_COUNTS;
}

/**
 * Context para que AdminLayout haga UN fetch y los nav items lo consuman
 * sin repetir queries por ítem.
 */
export const PendingCountsContext = createContext<PendingCounts>(EMPTY_PENDING_COUNTS);

export function usePendingCounts(): PendingCounts {
  return useContext(PendingCountsContext);
}

/** Count que corresponde a un ítem del sidebar (por pendingKey declarado en el nav). */
export function pendingCountForKey(
  counts: PendingCounts,
  key: "memberships" | "free" | undefined,
): number {
  if (key === "memberships") return counts.memberships_awaiting + counts.mp_pending;
  if (key === "free") return counts.free_pending;
  return 0;
}
